import axios from 'axios';
import { rsparseMatchState } from './coach_pipeline';

/**
 * Fills fields the Coach match components read that older backends omit:
 * `platform` (only `match_platform` before backend #1921) and `is_subscriber`.
 */
export function normalizeRsparseMap(data) {
  return {
    ...data,
    platform: data.platform ?? data.match_platform,
    overviews: (data.overviews || []).map((overview) => ({
      ...overview,
      is_subscriber: overview.is_subscriber ?? false,
    })),
  };
}

/**
 * One map of a pro series.
 *
 * Resolves to `{ processing, data }`; `data` is null while processing or when
 * rsparse has nothing for the map. Rejects when the request fails.
 */
export async function fetchProSeriesMap(matchUuid) {
  const { data } = await axios.get(`/coach/matches/${matchUuid}`);
  const state = rsparseMatchState(data);

  return {
    processing: state === 'processing',
    data: state === 'ready' ? normalizeRsparseMap(data) : null,
  };
}

function normalize(value) {
  return (value || '').toString().trim().toLowerCase();
}

function seriesPlayerNames(team) {
  return new Set(
    (team?.players || [])
      .flatMap((player) => [player?.nickname, player?.nick, player?.name])
      .filter(Boolean)
      .map(normalize),
  );
}

function countMembers(overviews, names) {
  return overviews.filter((overview) => names.has(normalize(overview?.display_name))).length;
}

export function escapeHtml(value) {
  return String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/**
 * The series teams on the rsparse `a`/`b` sides, matched by player name (pro
 * names arrive on `display_name`). Null when the roster can't tell the sides apart.
 */
export function seriesSideTeams(overviews, seriesTeams) {
  const team1 = seriesTeams?.team1;
  const team2 = seriesTeams?.team2;
  if (!team1?.name || !team2?.name || !overviews?.length) return null;

  const team1Names = seriesPlayerNames(team1);
  const team2Names = seriesPlayerNames(team2);
  const sideA = overviews.filter((overview) => overview?.side === 'a');
  const sideB = overviews.filter((overview) => overview?.side === 'b');

  const aIsTeam1 = countMembers(sideA, team1Names) + countMembers(sideB, team2Names);
  const aIsTeam2 = countMembers(sideA, team2Names) + countMembers(sideB, team1Names);
  if (aIsTeam1 === aIsTeam2) return null;

  return aIsTeam1 > aIsTeam2 ? { a: team1, b: team2 } : { a: team2, b: team1 };
}

/**
 * seriesSideTeams' names, HTML-escaped: StatsTable renders the label with v-html.
 */
export function seriesSideLabels(overviews, seriesTeams) {
  const teams = seriesSideTeams(overviews, seriesTeams);
  return teams && { a: escapeHtml(teams.a.name), b: escapeHtml(teams.b.name) };
}
